import {
  useCallback,
  useMemo,
  useState,
} from "@bestowinc/tinkertoy-shell-deps/react";
import type { ModuleID, VersionString } from "@bestowinc/tinkertoy-core";

import { useOptionalShellLoaderData } from "../router/use-shell-loader-data";
import { useShellRuntime } from "../shell-runtime";

export type LoadedModuleVersionRow = {
  moduleId: ModuleID;
  manifestVersion: VersionString | undefined;
  resolvedVersion: VersionString;
  isOverridden: boolean;
  overrideBundleUrl: string | undefined;
};

export function useLoadedVersionRows(): {
  viewId: string | undefined;
  viewVersion: VersionString | undefined;
  moduleRows: Array<LoadedModuleVersionRow>;
} {
  const data = useOptionalShellLoaderData();
  const { developerTools } = useShellRuntime();

  const moduleRows = useMemo(() => {
    if (data === undefined) {
      return [];
    }

    const manifestModules = data.loadedView.manifest.modules;
    const resolved = data.resolvedModuleVersions;
    const overrideModules = developerTools?.devOverrides?.modules ?? {};

    const ids = new Set<ModuleID>([
      ...Object.keys(manifestModules),
      ...Object.keys(resolved),
    ]);

    return [...ids]
      .sort()
      .flatMap((moduleId) => {
        const manifestEntry = manifestModules[moduleId];
        const resolvedVersion = resolved[moduleId] ?? manifestEntry?.version;
        if (resolvedVersion === undefined) {
          return [];
        }
        const overrideEntry = overrideModules[moduleId];
        return [
          {
            moduleId,
            manifestVersion: manifestEntry?.version,
            resolvedVersion,
            isOverridden: overrideEntry !== undefined,
            overrideBundleUrl: overrideEntry?.bundleUrl,
          },
        ];
      });
  }, [
    data?.loadedView.manifest.modules,
    data?.resolvedModuleVersions,
    developerTools?.devOverrides?.modules,
  ]);

  return {
    viewId: data?.loadedView.viewId,
    viewVersion: data?.loadedView.viewVersion,
    moduleRows,
  };
}

export function useDevToolsPanelState() {
  const [isOpen, setIsOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [pendingReload, setPendingReload] = useState(false);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  const noteMutationResult = useCallback((message: string, reloadRequired: boolean) => {
    setStatusMessage(message);
    setPendingReload(reloadRequired);
  }, []);

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    isOpen,
    toggle,
    statusMessage,
    pendingReload,
    noteMutationResult,
    reloadPage,
  };
}
